import { useCallback, useEffect, useState } from "react";
import { ClassicSoloView } from "../components/ClassicSoloView.tsx";
import { ScreenHeader } from "../components/ScreenHeader.tsx";
import { TimedSoloView } from "../components/TimedSoloView.tsx";
import {
  loadSoloMode,
  resolveSoloMode,
  saveSoloMode,
  type SoloMode,
} from "../features/solo/soloMode.ts";
import type { RegisterBackHandler } from "../lib/navigation.ts";
import { useOnline } from "../lib/pwa.ts";
import { useSession } from "../lib/session.ts";
import { messages } from "../i18n/index.ts";

interface PracticeScreenProps {
  onBack: () => void;
  onExitConfirmed: () => void;
  onSignIn: (reason: string) => void;
  registerBackHandler: RegisterBackHandler;
}

/**
 * Tek Başına Oyna: Klasik (sınırsız, çevrimdışı) ya da 3 Dakika (sunucuda,
 * arkadaş rekorlarıyla). Son seçilen tür hatırlanır; "Tekrar oyna" aynı türde
 * yeni oyun açar.
 */
export function PracticeScreen({
  onBack,
  onExitConfirmed,
  onSignIn,
  registerBackHandler,
}: PracticeScreenProps) {
  const status = useSession((store) => store.status);
  const online = useOnline();
  // Saklanan tercih 3 Dakika ise oturum bilgisi gelene kadar karar bekler.
  const [mode, setMode] = useState<SoloMode | null>(() =>
    loadSoloMode() === "classic" ? "classic" : null,
  );

  useEffect(() => {
    if (mode !== null || (online && status === "loading")) return;
    setMode(resolveSoloMode(loadSoloMode(), status, online));
  }, [mode, online, status]);

  const changeMode = useCallback((next: SoloMode) => {
    setMode(next);
    saveSoloMode(next);
  }, []);

  if (mode === null) {
    return (
      <div className="app">
        <ScreenHeader title={messages.solo.title} onBack={onBack} />
        <main className="grid place-items-center">
          <p className="text-sm text-muted">{messages.common.loading}</p>
        </main>
      </div>
    );
  }

  return mode === "timed" ? (
    <TimedSoloView
      onBack={onBack}
      onExitConfirmed={onExitConfirmed}
      onModeChange={changeMode}
      onSignIn={onSignIn}
      registerBackHandler={registerBackHandler}
    />
  ) : (
    <ClassicSoloView
      onBack={onBack}
      onExitConfirmed={onExitConfirmed}
      onModeChange={changeMode}
      registerBackHandler={registerBackHandler}
    />
  );
}
